// ── Design decisions ──
// Visor de la FUENTE de la extracción (la foto de la lista manuscrita que el
// operador compara fila por fila). Rediseño mobile-first: Margarita trabaja
// con una mano desde un teléfono de 390px, así que el teléfono manda y el
// escritorio es la mejora — al revés de la doctrina operador-escritorio, y a
// propósito.
//
// · Ajuste a lo ancho por defecto: la imagen entra SIEMPRE al ancho del marco
//   (w-full, nunca max-w-none) y el marco toma la altura del contenido hasta
//   un tope (40vh móvil / 60vh escritorio) — se acabó la caja de alto fijo
//   que recortaba fotos altas o dejaba relleno muerto alrededor de las bajas.
// · Zoom ADITIVO desde el ajuste: rango [1, 4]. Pellizco en el celular, rueda
//   o botones en escritorio; el zoom apunta al cursor / centro del pellizco
//   (no a la esquina), que es lo natural para leer manuscrita. "Ajustar"
//   vuelve al encuadre inicial de un toque.
// · Paneo por arrastre cuando hay de dónde panear. Con dedo solo se panea
//   estando acercado (touch-action: pan-y en el ajuste, así el pulgar sigue
//   scrolleando la página); con mouse siempre.
// · Colapsable: una barra fina con chevron —siempre alcanzable, sticky dentro
//   del scroll del panel en móvil— esconde la foto entera para que la lista
//   tome toda la pantalla, y la trae de vuelta sin perder ni el scroll ni el
//   zoom (el cuerpo se oculta con `hidden`, nunca se desmonta).
// · Sticky SOLO en escritorio (md:sticky): el bug del sticky incondicional
//   que clavaba la foto arriba de un viewport angosto murió con los estilos
//   inline.
// · Estados: sin foto → "Vista previa no disponible"; error de carga →
//   aviso + Reintentar (re-monta el <img> con un key).
// · La rueda se registra con addEventListener({ passive:false }) porque React
//   la registra passive y preventDefault no cortaría el scroll de la página.
// · El zoom vive ACÁ (antes era estado del padre): se reinicia solo al llegar
//   otra foto. Los gestos usan Pointer Events (mouse + dedo por igual).
// · 'tabla' (camino XLSX futuro) queda tal cual estaba: solo gana la barra de
//   colapso, que es chrome del panel, no de la tabla.
// · 'texto' (lista pegada de WhatsApp): el pegado crudo en monoespaciada con
//   número de línea, mismo marco colapsable, para comparar fuente contra
//   extracción línea por línea; arriba el chip "Análisis local — sin costo"
//   (acá y no en el drawer: esCached no aplica a un parseo local). 'pdf':
//   tarjeta con el nombre del archivo — una vista previa de la primera página
//   pediría una dependencia nueva (pdf.js), así que no. Ninguno de los dos
//   monta el visor de zoom/paneo: eso es solo de 'imagen'.
// · Piel Ambiance directa (sin prop): este panel sólo lo monta el
//   ExtractionDrawer, así que las clases `amb-*` no pueden filtrarse a otra
//   pantalla.

import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  FileText,
  Maximize2,
  Minimize2,
  RotateCcw,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { fmtUsd } from './shared/labels';

// Rango del zoom relativo al ajuste a lo ancho: 1 = la foto entra completa a
// lo ancho; no hay zoom-out por debajo (achicaría sin ganar nada).
export const ZOOM_MIN = 1;
export const ZOOM_MAX = 4;
export const ZOOM_PASO = 0.25;

const VISTA_INICIAL = { z: 1, x: 0, y: 0 };

const TablaFuente = ({ filas }) => {
  const lista = Array.isArray(filas) ? filas : [];
  if (lista.length === 0) {
    return <p className="amb-empty border-0 bg-transparent">La tabla de origen está vacía</p>;
  }
  return (
    <table className="w-full text-xs" aria-label="Tabla de origen">
      <thead className="amb-src-th amb-thead sticky top-0">
        <tr>
          <th scope="col" className="px-3 py-2 text-left">
            Descripción
          </th>
          <th scope="col" className="px-3 py-2 text-right">
            Precio
          </th>
        </tr>
      </thead>
      <tbody>
        {lista.map((f, i) => (
          <tr key={i} className="amb-src-td">
            <td className="px-3 py-2 text-[13px]">{f.descripcion ?? ''}</td>
            <td className="amb-in--mono px-3 py-2 text-right text-[13px]">
              {f.precio === null || f.precio === undefined || f.precio === '' ? '—' : `$${fmtUsd(f.precio)}`}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const ROTULOS = {
  imagen: 'Foto de la lista',
  tabla: 'Tabla de la lista',
  texto: 'Texto de la lista',
  pdf: 'Documento PDF',
};
// La barra de colapso nombra la fuente en el aria-label; 'la fuente' cubre
// texto y pdf sin inventar más pares de frases.
const NOMBRE_FUENTE = { imagen: 'la foto', tabla: 'la tabla', texto: 'el texto', pdf: 'el documento' };

// Pegado crudo con número de línea: el operador coteja contra las filas
// extraídas. Las líneas vacías se conservan (la numeración tiene que coincidir
// con lo que se pegó).
const TextoFuente = ({ texto }) => {
  const lineas = String(texto ?? '').split(/\r\n|\r|\n/);
  if (!String(texto ?? '').trim()) {
    return <p className="amb-empty border-0 bg-transparent">No hay texto pegado</p>;
  }
  return (
    <div className="px-3 py-2">
      <p className="mb-2">
        <span className="amb-chip amb-chip--neutral" title="La lista se analizó en este dispositivo, sin llamar a la IA">
          Análisis local — sin costo
        </span>
      </p>
      <ol data-testid="texto-lista" aria-label="Texto pegado de la lista" className="amb-in--mono m-0 list-none p-0 text-[12px] leading-5">
        {lineas.map((l, i) => (
          <li key={i} className="flex gap-2 whitespace-pre-wrap break-words">
            <span aria-hidden="true" className="w-6 shrink-0 select-none text-right opacity-40">
              {i + 1}
            </span>
            <span className="min-w-0">{l || ' '}</span>
          </li>
        ))}
      </ol>
    </div>
  );
};

const SourcePane = ({ tipo = 'imagen', imageUrl = null, sourceMeta = null, titulo = null }) => {
  const esTabla = tipo === 'tabla';
  const esTexto = tipo === 'texto';
  const esPdf = tipo === 'pdf';
  const esImagen = !esTabla && !esTexto && !esPdf;
  const rotulo = titulo || ROTULOS[tipo] || ROTULOS.imagen;
  const nombreFuente = NOMBRE_FUENTE[tipo] || NOMBRE_FUENTE.imagen;
  const idCuerpo = useId();

  const [colapsado, setColapsado] = useState(false);
  const [ampliado, setAmpliado] = useState(false); // solo móvil: visor casi a pantalla completa
  const [vista, setVista] = useState(VISTA_INICIAL);
  const [fallo, setFallo] = useState(false);
  const [reintento, setReintento] = useState(0);

  const vpRef = useRef(null); // viewport (marco con overflow hidden)
  const aspectoRef = useRef(null); // alto natural / ancho natural, llega con onLoad
  const punteros = useRef(new Map());
  const pinza = useRef(null);
  const arrastre = useRef(null);
  const vistaRef = useRef(vista);
  vistaRef.current = vista;

  // Otra foto ⇒ visor limpio y visible.
  useEffect(() => {
    setVista(VISTA_INICIAL);
    setFallo(false);
    setColapsado(false);
    setAmpliado(false);
    aspectoRef.current = null;
    punteros.current.clear();
    pinza.current = null;
    arrastre.current = null;
  }, [imageUrl, tipo]);

  // El contenido nunca se arrastra fuera del marco: el borde interno del
  // contenido no pasa del borde del viewport. Si el contenido cabe, queda en 0.
  const clampVista = useCallback((v) => {
    const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, v.z));
    const vp = vpRef.current;
    if (!vp) return { z, x: 0, y: 0 };
    const vw = vp.clientWidth;
    const vh = vp.clientHeight;
    const bw = vw; // base = ajuste a lo ancho
    const bh = aspectoRef.current ? vw * aspectoRef.current : vh;
    const cw = bw * z;
    const ch = bh * z;
    const x = cw > vw ? Math.min(0, Math.max(vw - cw, v.x)) : 0;
    const y = ch > vh ? Math.min(0, Math.max(vh - ch, v.y)) : 0;
    return { z, x, y };
  }, []);

  // Zoom hacia un punto (px, py) del viewport: lo que está bajo el cursor o el
  // centro del pellizco no se mueve. k = z'/z.
  const zoomHacia = useCallback(
    (zObjetivo, px, py) => {
      setVista((v) => {
        const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, zObjetivo));
        const k = z / v.z;
        return clampVista({ z, x: px - (px - v.x) * k, y: py - (py - v.y) * k });
      });
    },
    [clampVista]
  );

  const zoomBoton = (delta) => {
    const vp = vpRef.current;
    const cx = vp ? vp.clientWidth / 2 : 0;
    const cy = vp ? vp.clientHeight / 2 : 0;
    zoomHacia(+(vistaRef.current.z + delta).toFixed(2), cx, cy);
  };

  const reiniciar = () => setVista(VISTA_INICIAL);

  // Rueda: zoom hacia el cursor. Nativo y passive:false (ver cabecera).
  // Solo 'imagen' monta el viewport; en el resto vpRef queda null igual.
  useEffect(() => {
    const vp = vpRef.current;
    if (!vp || !esImagen) return undefined;
    const onWheel = (e) => {
      e.preventDefault();
      const r = vp.getBoundingClientRect();
      const factor = Math.exp(-e.deltaY * 0.0022);
      zoomHacia(vistaRef.current.z * factor, e.clientX - r.left, e.clientY - r.top);
    };
    vp.addEventListener('wheel', onWheel, { passive: false });
    return () => vp.removeEventListener('wheel', onWheel);
  }, [esImagen, zoomHacia]);

  // Pointer Events: 1 puntero = paneo, 2 = pellizco. Con dedo el paneo solo
  // corre estando acercado; en el ajuste el gesto vertical scrollea la página
  // (touch-action: pan-y), que es lo que una mano espera.
  const anclarArrastre = (px, py, pointerType) => {
    const permitido = pointerType === 'mouse' || vistaRef.current.z > ZOOM_MIN;
    arrastre.current = permitido
      ? { px, py, x0: vistaRef.current.x, y0: vistaRef.current.y }
      : null;
  };

  const onPointerDown = (e) => {
    const vp = vpRef.current;
    if (!vp || !imageUrl || fallo) return;
    vp.setPointerCapture?.(e.pointerId);
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY, tipo: e.pointerType });
    const pts = [...punteros.current.values()];
    if (pts.length === 2) {
      const r = vp.getBoundingClientRect();
      pinza.current = {
        d0: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1,
        m0x: (pts[0].x + pts[1].x) / 2 - r.left,
        m0y: (pts[0].y + pts[1].y) / 2 - r.top,
        z0: vistaRef.current.z,
        x0: vistaRef.current.x,
        y0: vistaRef.current.y,
      };
      arrastre.current = null;
    } else if (pts.length === 1) {
      anclarArrastre(e.clientX, e.clientY, e.pointerType);
    }
  };

  const onPointerMove = (e) => {
    if (!punteros.current.has(e.pointerId)) return;
    punteros.current.set(e.pointerId, { x: e.clientX, y: e.clientY, tipo: e.pointerType });
    const pts = [...punteros.current.values()];
    if (pts.length >= 2 && pinza.current) {
      const vp = vpRef.current;
      if (!vp) return;
      const r = vp.getBoundingClientRect();
      const d = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
      const mx = (pts[0].x + pts[1].x) / 2 - r.left;
      const my = (pts[0].y + pts[1].y) / 2 - r.top;
      const { d0, m0x, m0y, z0, x0, y0 } = pinza.current;
      const z = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z0 * (d / d0)));
      const k = z / z0;
      setVista(clampVista({ z, x: mx - (m0x - x0) * k, y: my - (m0y - y0) * k }));
    } else if (pts.length === 1 && arrastre.current) {
      const { px, py, x0, y0 } = arrastre.current;
      const nx = x0 + (e.clientX - px);
      const ny = y0 + (e.clientY - py);
      setVista((v) => clampVista({ z: v.z, x: nx, y: ny }));
    }
  };

  const soltarPuntero = (e) => {
    punteros.current.delete(e.pointerId);
    pinza.current = null;
    const restantes = [...punteros.current.values()];
    if (restantes.length === 1) {
      // 2 → 1: se re-ancla el paneo desde el dedo que quedó.
      anclarArrastre(restantes[0].x, restantes[0].y, restantes[0].tipo);
    } else {
      arrastre.current = null;
    }
  };

  const enAjuste = vista.z === 1 && vista.x === 0 && vista.y === 0;
  const alturaVisor = ampliado ? 'max-h-[78vh] md:max-h-[60vh]' : 'max-h-[40vh] md:max-h-[60vh]';

  return (
    <section
      aria-label="Fuente de la lista"
      data-testid="source-pane"
      className="flex w-full flex-col gap-2 md:sticky md:top-0 md:w-2/5 md:self-start"
    >
      {/* Barra de colapso: siempre alcanzable (sticky dentro del scroll del
          panel en móvil; en escritorio el pane entero ya es sticky). */}
      <div className="amb-src-head sticky top-0 z-20 md:static">
        <button
          type="button"
          onClick={() => setColapsado((c) => !c)}
          aria-expanded={!colapsado}
          aria-controls={idCuerpo}
          aria-label={colapsado ? `Mostrar ${nombreFuente}` : `Ocultar ${nombreFuente}`}
          title={colapsado ? 'Mostrar la fuente' : 'Ocultar la fuente para ver la lista completa'}
          className="amb-src-toggle"
        >
          {colapsado ? (
            <ChevronRight size={15} aria-hidden="true" />
          ) : (
            <ChevronDown size={15} aria-hidden="true" />
          )}
          <span className="amb-eyebrow truncate">{rotulo}</span>
        </button>
      </div>

      <div id={idCuerpo} hidden={colapsado} className="flex flex-col gap-2">
        {esTabla ? (
          <div aria-label="Tabla de la lista" role="region" className="amb-frame">
            <div className="amb-frame-inner amb-scroll h-72 overflow-auto md:h-[60vh]">
              <TablaFuente filas={sourceMeta?.filas} />
            </div>
          </div>
        ) : esTexto ? (
          <div aria-label="Texto de la lista" role="region" className="amb-frame">
            <div className="amb-frame-inner amb-scroll max-h-[40vh] overflow-auto md:max-h-[60vh]">
              <TextoFuente texto={sourceMeta?.texto} />
            </div>
          </div>
        ) : esPdf ? (
          <div aria-label="Documento PDF" role="region" className="amb-frame">
            <div className="amb-frame-inner flex items-center gap-3 px-4 py-6">
              <FileText size={28} aria-hidden="true" className="shrink-0 opacity-60" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold" data-testid="pdf-nombre">
                  {sourceMeta?.file_name || 'documento.pdf'}
                </p>
                <p className="amb-meta">PDF analizado — sin vista previa</p>
              </div>
            </div>
          </div>
        ) : (
          <div aria-label="Foto de la lista" role="region" className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-1.5">
              <button
                type="button"
                onClick={() => zoomBoton(-ZOOM_PASO)}
                aria-label="Alejar"
                title="Alejar"
                disabled={!imageUrl || fallo || vista.z <= ZOOM_MIN}
                className="amb-ibtn amb-ibtn--src"
              >
                <ZoomOut size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={() => zoomBoton(ZOOM_PASO)}
                aria-label="Acercar"
                title="Acercar"
                disabled={!imageUrl || fallo || vista.z >= ZOOM_MAX}
                className="amb-ibtn amb-ibtn--src"
              >
                <ZoomIn size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={reiniciar}
                aria-label="Ajustar a lo ancho"
                title="Volver al encuadre inicial"
                disabled={!imageUrl || fallo || enAjuste}
                className="amb-ibtn amb-ibtn--src"
              >
                <RotateCcw size={13} aria-hidden="true" />
              </button>
              <span className="amb-meta">{Math.round(vista.z * 100)}%</span>
              <button
                type="button"
                onClick={() => setAmpliado((a) => !a)}
                aria-label={ampliado ? 'Reducir el visor' : 'Ampliar el visor'}
                title={ampliado ? 'Volver al visor compacto' : 'Ampliar para leer de cerca'}
                disabled={!imageUrl || fallo}
                className="amb-ibtn amb-ibtn--src ml-auto md:hidden"
              >
                {ampliado ? (
                  <Minimize2 size={14} aria-hidden="true" />
                ) : (
                  <Maximize2 size={14} aria-hidden="true" />
                )}
              </button>
            </div>
            <div className="amb-frame">
              <div
                ref={vpRef}
                data-testid="visor-foto"
                className={`amb-frame-inner relative w-full overflow-hidden ${alturaVisor}`}
                style={{
                  touchAction: vista.z > 1 ? 'none' : 'pan-y',
                  cursor: imageUrl && !fallo && vista.z > 1 ? 'grab' : undefined,
                }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={soltarPuntero}
                onPointerCancel={soltarPuntero}
              >
                {imageUrl && !fallo ? (
                  <img
                    key={reintento}
                    src={imageUrl}
                    alt="Lista de encomienda subida"
                    data-testid="imagen-lista"
                    draggable={false}
                    onLoad={(e) => {
                      const el = e.currentTarget;
                      if (el.naturalWidth > 0) aspectoRef.current = el.naturalHeight / el.naturalWidth;
                    }}
                    onError={() => setFallo(true)}
                    className="block w-full select-none"
                    style={{
                      transform: `translate(${vista.x}px, ${vista.y}px) scale(${vista.z})`,
                      transformOrigin: '0 0',
                    }}
                  />
                ) : imageUrl && fallo ? (
                  <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                    <p className="amb-status amb-status--error">No se pudo cargar la foto.</p>
                    <button
                      type="button"
                      onClick={() => {
                        setFallo(false);
                        setReintento((n) => n + 1);
                      }}
                      className="amb-btn amb-btn--secondary"
                    >
                      Reintentar
                    </button>
                  </div>
                ) : (
                  <p className="amb-empty border-0 bg-transparent">Vista previa no disponible</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SourcePane;
